import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportModal = ({ isOpen, onClose, onExport }) => {
  const { t } = useTranslation("pages");
  const [exportConfig, setExportConfig] = useState({
    format: 'pdf',
    dateFrom: '',
    dateTo: '',
    includeDetails: true,
    includeMetadata: false,
    columns: {
      timestamp: true,
      user: true,
      action: true,
      resource: true,
      ipAddress: true,
      status: true
    }
  });

  const formatOptions = [
    { value: 'pdf', label: t("auditExport.formats.pdf") },
    { value: 'csv', label: t("auditExport.formats.csv") },
    { value: 'json', label: t("auditExport.formats.json") }
  ];

  const handleConfigChange = (key, value) => {
    setExportConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleColumnChange = (column, checked) => {
    setExportConfig(prev => ({
      ...prev,
      columns: {
        ...prev?.columns,
        [column]: checked
      }
    }));
  };

  const handleExport = () => {
    onExport(exportConfig);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            {t("auditExport.title")}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <Select
              label={t("auditExport.fields.format")}
              options={formatOptions}
              value={exportConfig?.format}
              onChange={(value) => handleConfigChange('format', value)}
            />
          </div>

          {/* Date Range */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">
              {t("auditExport.dateRange")}
            </h4>
            <div className="grid grid-cols-1 gap-4">
              <Input
                label={t("auditExport.fields.fromDate")}
                type="date"
                value={exportConfig?.dateFrom}
                onChange={(e) => handleConfigChange('dateFrom', e?.target?.value)}
              />
              <Input
                label={t("auditExport.fields.toDate")}
                type="date"
                value={exportConfig?.dateTo}
                onChange={(e) => handleConfigChange('dateTo', e?.target?.value)}
              />
            </div>
          </div>

          {/* Export Options */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">
              {t("auditExport.options.exportOptions")}
            </h4>
            <div className="space-y-3">
              <Checkbox
                label={t("auditExport.options.includeDetails")}
                checked={exportConfig?.includeDetails}
                onChange={(e) => handleConfigChange('includeDetails', e?.target?.checked)}
              />
              <Checkbox
                label={t("auditExport.options.includeMetadata")}
                checked={exportConfig?.includeMetadata}
                onChange={(e) => handleConfigChange('includeMetadata', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Column Selection */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">
              {t("auditExport.options.columns")}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <Checkbox
                label={t("auditExport.columns.timestamp")}
                checked={exportConfig?.columns?.timestamp}
                onChange={(e) => handleColumnChange('timestamp', e?.target?.checked)}
              />
              <Checkbox
                label={t("auditExport.columns.user")}
                checked={exportConfig?.columns?.user}
                onChange={(e) => handleColumnChange('user', e?.target?.checked)}
              />
              <Checkbox
                label={t("auditExport.columns.action")}
                checked={exportConfig?.columns?.action}
                onChange={(e) => handleColumnChange('action', e?.target?.checked)}
              />
              <Checkbox
                label={t("auditExport.columns.resource")}
                checked={exportConfig?.columns?.resource}
                onChange={(e) => handleColumnChange('resource', e?.target?.checked)}
              />
              <Checkbox
                label={t("auditExport.columns.ipAddress")}
                checked={exportConfig?.columns?.ipAddress}
                onChange={(e) => handleColumnChange('ipAddress', e?.target?.checked)}
              />
              <Checkbox
                label={t("auditExport.columns.status")}
                checked={exportConfig?.columns?.status}
                onChange={(e) => handleColumnChange('status', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            variant="default"
            onClick={handleExport}
            iconName="Download"
            iconPosition="left"
          >
            {t("auditExport.actions.export")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;

